import { useEffect } from 'react';
import { findMe, refreshDevice, followMe, markAsked, whatToDoOnOpen } from './place.js';

/* ══════════════════════════════════════════════════════════════════════════
 * ELEAT ASKS FOR YOUR LOCATION ONCE. THE PHONE ASKS. NOTHING ELSE DOES.
 *
 * ⚠️⚠️⚠️ AL, 28 SEPTEMBER 2026, ON THE LIVE SITE — AND HE HAS SAID IT BEFORE:
 *
 *   *"you're asking to allow to pick up your location and precise location.
 *   And then you're going back into the fucking app and asking them, can't
 *   find you or this, this, and this. Do you want to use precise or do you
 *   want to pick a location? Why do you have that? I've asked you so many
 *   fucking times to remove it… It simply says, use your fucking location and
 *   that's it. If they want, they can go in and click the map button or the
 *   balloon in the top bar, and they can change the location after that."*
 *
 * ⚠️ SO THIS SCREEN DRAWS NOTHING. NOT A SHEET, NOT A BUTTON, NOT AN ERROR.
 * It asks the phone, and the phone's own permission prompt is the only thing
 * an Eleater ever sees. That is the whole component.
 *
 * ── ⚠️ WHAT WAS HERE, AND WHY IT WAS WRONG ────────────────────────────────
 *
 * A full-screen sheet — *"Eleat shows you what is on here"* — with **Use my
 * location** and **Choose a place**, shown BEFORE the phone's prompt; and on a
 * failure it stayed on screen carrying the error. So the journey was:
 *
 *     Eleat's panel → the phone's prompt → Eleat's panel again, with a problem
 *
 * **Three interruptions before a restaurant.** On his own phone that reads as
 * an app that cannot find you and keeps asking.
 *
 * ⚠️ AND MY JUSTIFICATION FOR IT IS THE PART WORTH RECORDING. The note that
 * used to sit here cited web.dev on priming a permission prompt, and Uber's
 * manual-entry fallback, and both citations are real. **They answer the
 * question "does a primer raise the accept rate", which nobody asked.** The
 * question was what Eleat does when somebody opens it. Al answered that one
 * repeatedly and I kept producing evidence for the other one. Same shape as
 * *"our locations rows are MORE ACCURATE than a general geocoder"* — true
 * about one job, and used to settle a different job. **Research is not an
 * authority over his instruction (RULE 3).**
 *
 * ⚠️ AND NOTHING IS LOST BY IT. The other way in is not deleted, it has just
 * stopped being a panel in the way: the PIN IN THE TOP BAR opens the Location
 * screen, which is where choosing a city, searching a place and browsing by
 * area already live. That is the button Al names above.
 *
 * ⚠️ A REFUSAL IS SILENT AND PERMANENT, AND THAT IS NOT A BUG TO PAPER OVER.
 * A browser records Don't Allow and never asks again — no amount of UI gets it
 * back, so a panel explaining the refusal is a panel that can change nothing.
 * Eleat carries on with no centre; every screen already handles that (the five
 * headlines that name a place simply do not show), and the pin reads *"Set
 * location"*, which is the invitation, in its proper place.
 * ══════════════════════════════════════════════════════════════════════════ */

export default function LocationAsk() {
  useEffect(() => {
    let alive = true;
    let unfollow = () => {};

    whatToDoOnOpen().then((what) => {
      if (!alive) return;

      /* ⚠️ 'ask' NO LONGER MEANS "SHOW A SHEET". It means the browser has not
         been asked yet — so ask it. `findMe()` is what triggers the phone's
         own prompt, and it is now the FIRST and ONLY thing that happens. */
      if (what === 'ask') {
        findMe()
          .then(() => {
            if (!alive) return;
            markAsked('granted');
            /* The moment they say yes it starts following, rather than waiting
               for the next app open — the first session is the one they are
               most likely to be walking through. */
            unfollow = followMe();
          })
          .catch(() => {
            /* ⚠️ NOTHING ON SCREEN. `findMe` has already recorded 'denied' or
               'failed' itself, so this is not swallowed — it is stored, and
               `whatToDoOnOpen` reads it next time and does not ask again.
               Al's instruction is that a person who said no is left alone. */
          });
        return;
      }

      if (what === 'silent') {
        /* ⚠️ PERMISSION IS ALREADY GRANTED — no prompt will appear, so this
           just happens and the pin fills in.
           ⚠️ `refreshDevice()`, NOT `findMe()`. Both ask the phone the same
           question; only one overwrites a centre the Eleater chose. Al, 25
           September 2026: *"physical location = Shenzhen, selected discovery =
           Hong Kong must continue showing Hong Kong until I choose Current
           Location or another destination."* */
        refreshDevice().catch(() => { /* a wobble is not worth a panel; the pin waits */ });
      }

      /* ⚠️ AND THEN IT KEEPS UP WITH HIM. Al, 24 September 2026: *"if I walk
         100 meters left, right, up or down… recheck my location."*  Without
         this the app is a snapshot: open in TST, ride to Central, still says
         TST. It records where the PHONE is however far away they are browsing,
         because Directions needs that even for a city they have never been to.
         ⚠️ 'choose' starts nothing — there is no permission to watch with. */
      if (what === 'have' || what === 'silent') unfollow = followMe();
    });

    return () => { alive = false; unfollow(); };
  }, []);

  /* ⚠️ IT RENDERS NOTHING, ON EVERY PATH, FOR EVERY PERSON. If this ever grows
     a panel again, read Al's words at the top of this file first. */
  return null;
}
